import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { PageHeader } from '@/components/shared/PageHeader';
import { PoolWalletsTable } from '@/components/features/wallets/PoolWalletsTable';
import { PoolWalletRetireDialog } from '@/components/features/wallets/PoolWalletRetireDialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Breadcrumbs } from '@/components/shared/Breadcrumbs';
import { LoadingState } from '@/components/shared/LoadingState';
import { ErrorState } from '@/components/shared/ErrorState';
import { usePoolWallets, useRefreshPoolWalletBalance } from '@/lib/hooks';
import { formatCurrency } from '@/utils/formatters';
import { ROUTES } from '@/config/routes';
import type { Wallet } from '@/lib/types';

export const Route = createFileRoute('/_auth/_admin/admin/wallets/pool')({
  component: PoolWalletsComponent,
});

function PoolWalletsComponent() {
  const { data, isLoading, isError, error } = usePoolWallets();
  const refreshBalance = useRefreshPoolWalletBalance();
  const [refreshingId, setRefreshingId] = useState<number | null>(null);
  const [retireTarget, setRetireTarget] = useState<Wallet | null>(null);

  const breadcrumbs = [
    { label: 'Кошельки', href: ROUTES.ADMIN.WALLETS },
    { label: 'Пул кошельков' },
  ];

  const handleRefreshBalance = (id: number) => {
    setRefreshingId(id);
    refreshBalance.mutate(id, {
      onSettled: () => setRefreshingId(null),
    });
  };

  if (isLoading) {
    return <LoadingState />;
  }

  if (isError) {
    return <ErrorState title={error.name} message={error.message} />;
  }

  const poolData = data;

  return (
    <div className="space-y-6">
      <Breadcrumbs items={breadcrumbs} />
      
      <PageHeader
        title="Пул адресов"
        description=""
      />

      <div className="grid gap-6 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>Общий баланс</CardTitle>
            <CardDescription>Общий баланс всего пула</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">
              {formatCurrency(poolData!.total_balance)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Свободны</CardTitle>
            <CardDescription>Готовы для депозита</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-green-600">
              {poolData?.available_wallets}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Заняты</CardTitle>
            <CardDescription>Сейчас используются</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-orange-600">
              {poolData?.locked_wallets}
            </p>
          </CardContent>
        </Card>
      </div>

      <PoolWalletsTable
        wallets={poolData!.wallets}
        onRefreshBalance={handleRefreshBalance}
        onRetire={setRetireTarget}
        refreshingId={refreshingId}
      />

      <PoolWalletRetireDialog
        open={retireTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRetireTarget(null);
        }}
        wallet={retireTarget}
      />
    </div>
  );
}
